import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

const Section = ({ title, children }) => (
  <section className="mb-5">
    <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2">
      {title}
    </h3>
    <div className="text-sm md:text-base text-gray-700 leading-relaxed space-y-2">
      {children}
    </div>
  </section>
);

const PrivacyPolicyModal = ({ open, onOpenChange }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-y-auto bg-white"
        data-testid="privacy-policy-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl md:text-3xl font-extrabold text-gray-900">
            Aviso de Privacidad
          </DialogTitle>
          <DialogDescription className="text-orange-600 font-semibold">
            GenSolver · www.gensolver.com
          </DialogDescription>
          <p className="text-xs text-gray-500 italic">
            Fecha de última actualización: Abril 2026
          </p>
        </DialogHeader>

        <div className="mt-4">
          <Section title="1. Identidad y Domicilio del Responsable">
            <p>
              GenSolver (en adelante "el Responsable"), con presencia en
              Veracruz y Sureste de México, es responsable del tratamiento de
              sus datos personales conforme a lo establecido en la Ley Federal
              de Protección de Datos Personales en Posesión de los Particulares
              (LFPDPPP) y su Reglamento.
            </p>
            <p>
              <strong>Sitio web:</strong> www.gensolver.com
              <br />
              <strong>Correo de contacto:</strong> contacto@gensolver.com
            </p>
          </Section>

          <Section title="2. Datos Personales que Recabamos">
            <p>A través de nuestro sitio web podemos recabar los siguientes datos:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Nombre completo</li>
              <li>Correo electrónico</li>
              <li>Número de teléfono</li>
              <li>Empresa u organización</li>
              <li>Mensaje o descripción de su requerimiento</li>
            </ul>
            <p className="mt-2">
              Datos recabados de forma automática mediante cookies y tecnologías
              similares:
            </p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Dirección IP</li>
              <li>Tipo y versión del navegador</li>
              <li>Páginas visitadas y tiempo de navegación</li>
              <li>Interacciones con anuncios publicitarios</li>
            </ul>
          </Section>

          <Section title="3. Finalidades del Tratamiento">
            <p>
              Sus datos personales serán utilizados para las siguientes
              finalidades primarias (necesarias para la relación comercial):
            </p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Atender solicitudes de cotización y contacto</li>
              <li>
                Brindar información sobre nuestros servicios de renta de equipos
                electrógenos
              </li>
              <li>Dar seguimiento a sus requerimientos comerciales</li>
            </ul>
            <p className="mt-2">
              Finalidades secundarias (puede oponerse a éstas):
            </p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Envío de información comercial y promociones de GenSolver</li>
              <li>
                Análisis estadísticos de uso del sitio web para mejoras internas
              </li>
            </ul>
          </Section>

          <Section title="4. Google Ads, Remarketing y Cookies de Terceros">
            <p>
              Este sitio web utiliza Google Ads para mostrar anuncios
              publicitarios personalizados. A través de la etiqueta de Google
              (gtag.js), Google puede usar cookies para mostrar anuncios de
              GenSolver a usuarios que han visitado previamente nuestro sitio
              (remarketing), incluso cuando navegan en otros sitios web o
              aplicaciones de la Red de Display de Google.
            </p>
            <p>Lo anterior implica que Google puede:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>
                Colocar cookies de conversión y remarketing (_gcl_au) en su
                dispositivo para identificar visitas anteriores a nuestro sitio
              </li>
              <li>
                Asociar su actividad de navegación con su cuenta de Google (si
                está iniciada sesión)
              </li>
              <li>
                Mostrar anuncios de GenSolver basados en sus visitas previas a
                gensolver.com en otros sitios de la red de Google
              </li>
            </ul>
            <p className="mt-2">
              Usted puede optar por no recibir anuncios personalizados de Google
              visitando:{' '}
              <a
                href="https://adssettings.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-600 underline hover:text-orange-700"
              >
                https://adssettings.google.com
              </a>{' '}
              o usando el Administrador de preferencias de anuncios de Google.
            </p>
            <p>
              La política de privacidad de Google está disponible en:{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-600 underline hover:text-orange-700"
              >
                https://policies.google.com/privacy
              </a>
            </p>
          </Section>

          <Section title="5. Google Analytics">
            <p>
              Este sitio utiliza Google Analytics, un servicio de análisis web
              de Google LLC. Google Analytics emplea cookies para ayudarnos a
              analizar el uso del sitio. La información generada (incluyendo su
              dirección IP anonimizada) es transmitida a servidores de Google en
              Estados Unidos.
            </p>
            <p>
              Puede inhabilitar Google Analytics instalando el complemento de
              inhabilitación disponible en:{' '}
              <a
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-600 underline hover:text-orange-700"
              >
                https://tools.google.com/dlpage/gaoptout
              </a>
            </p>
          </Section>

          <Section title="6. Uso de Cookies">
            <p>
              Nuestro sitio web utiliza cookies propias y de terceros. Usted
              puede configurar su navegador para rechazar todas las cookies o
              para que le avise cuando se envíe una cookie. Sin embargo, si
              rechaza las cookies, es posible que algunas partes del sitio no
              funcionen correctamente.
            </p>
            <p>Tipos de cookies que utilizamos:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>
                <strong>Cookies técnicas:</strong> necesarias para el
                funcionamiento del sitio
              </li>
              <li>
                <strong>Cookies analíticas:</strong> Google Analytics para medir
                el tráfico
              </li>
              <li>
                <strong>Cookies publicitarias:</strong> Google Ads y remarketing
                para personalizar anuncios
              </li>
            </ul>
          </Section>

          <Section title="7. Transferencia de Datos Personales">
            <p>Sus datos personales podrán ser compartidos con:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>
                <strong>Google LLC:</strong> para los servicios de Google
                Analytics y Google Ads descritos en este Aviso
              </li>
              <li>Autoridades competentes cuando así lo exija la ley</li>
            </ul>
            <p className="mt-2">
              No vendemos, alquilamos ni compartimos sus datos personales con
              terceros con fines comerciales propios sin su consentimiento
              previo.
            </p>
          </Section>

          <Section title="8. Derechos ARCO">
            <p>
              Usted tiene derecho a Acceder, Rectificar, Cancelar u Oponerse
              (derechos ARCO) al tratamiento de sus datos personales. Para
              ejercer estos derechos, envíe su solicitud a:
            </p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>
                <strong>Correo electrónico:</strong>{' '}
                <a
                  href="mailto:contacto@gensolver.com"
                  className="text-orange-600 underline hover:text-orange-700"
                >
                  contacto@gensolver.com
                </a>
              </li>
              <li>
                <strong>Asunto del correo:</strong> "Ejercicio de Derechos ARCO"
              </li>
            </ul>
            <p className="mt-2">
              Responderemos a su solicitud en un plazo máximo de 20 días hábiles
              conforme a lo establecido en la LFPDPPP.
            </p>
          </Section>

          <Section title="9. Cambios al Aviso de Privacidad">
            <p>
              Nos reservamos el derecho de modificar este Aviso de Privacidad en
              cualquier momento. Cualquier cambio será publicado en esta misma
              página con la fecha de actualización correspondiente. Le
              recomendamos revisar este aviso periódicamente.
            </p>
          </Section>

          <Section title="10. Consentimiento">
            <p>
              Al utilizar nuestro sitio web y/o al enviar sus datos a través del
              formulario de contacto, usted otorga su consentimiento para el
              tratamiento de sus datos personales conforme a los términos
              descritos en el presente Aviso de Privacidad.
            </p>
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrivacyPolicyModal;
